import { useEffect, useRef, useState } from 'react'
import type { SectionEditorProps } from './types'

interface Props extends SectionEditorProps<string> {
  placeholder?: string
}

/**
 * Multiline prose editor for description / why / what / open_questions /
 * custom-section bodies. Auto-grows as the user types; commits on blur or
 * after a short typing pause.
 *
 * The look-and-feel target is Granola: a soft warm background, no hard border,
 * comfortable typography. Click anywhere to type.
 */
export function ProseSection({ value, onChange, placeholder, mode: _mode }: Props) {
  const ref = useRef<HTMLTextAreaElement | null>(null)
  const [draft, setDraft] = useState(value ?? '')
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Reflect external changes (e.g. switching to a different requirement).
  useEffect(() => {
    setDraft(value ?? '')
  }, [value])

  // Resize on each draft change so the textarea hugs content.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [draft])

  function commit(next: string) {
    if (next === (value ?? '')) return
    onChange(next)
  }

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const next = e.target.value
    setDraft(next)
    if (debounce.current) clearTimeout(debounce.current)
    debounce.current = setTimeout(() => commit(next), 600)
  }

  function handleBlur() {
    if (debounce.current) {
      clearTimeout(debounce.current)
      debounce.current = null
    }
    commit(draft)
  }

  return (
    <textarea
      ref={ref}
      value={draft}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder ?? 'Write something…'}
      rows={1}
      className="w-full bg-transparent outline-none border-0 resize-none"
      style={{
        fontFamily: 'var(--font-sans)',
        fontSize: 14.5,
        lineHeight: 1.6,
        color: 'var(--color-fg)',
        padding: '4px 0',
      }}
    />
  )
}
